import { Component, effect, inject, input, model, output, ViewChild } from '@angular/core';
import { DropDownButtonComponent } from '@progress/kendo-angular-buttons';
import { NumberFormatOptions, NumberPipe } from '@progress/kendo-angular-intl';
import { FormFieldComponent, NumericTextBoxComponent, TextBoxComponent } from '@progress/kendo-angular-inputs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  ActionListItemModel,
  ActionMenuItemAction,
  AMOUNT_DECIMALS,
  AMOUNT_FORMAT,
  CapitalizePipe,
  ClientInvoiceDraftItem,
  ClientInvoiceDraftItemService,
  conditionStringValidator,
  DisabledDirective,
  DropdownLoaderDirective,
  errorToast,
  FormErrorService,
  getMergedListForIri,
  PercentagePipe,
  successDeleteToast,
  successSaveToast,
  SupplierArticle,
  SupplierArticleService,
  ValidationDirective,
} from 'shared';
import { catchError, concat, concatMap, debounceTime, EMPTY, map, Observable, of, Subject, take, tap } from 'rxjs';
import { NotificationService } from '@progress/kendo-angular-notification';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { ComboBoxComponent, DropDownFilterSettings, FilterDirective } from '@progress/kendo-angular-dropdowns';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import { RouterLinkActive } from '@angular/router';

@Component({
  selector: 'rc-client-invoice-draft-item',
  imports: [
    DisabledDirective,
    PercentagePipe,
    NumberPipe,
    NumericTextBoxComponent,
    ReactiveFormsModule,
    ValidationDirective,
    TextBoxComponent,
    FormFieldComponent,
    TranslateModule,
    AsyncPipe,
    ComboBoxComponent,
    FilterDirective,
    DropDownButtonComponent,
    CapitalizePipe,
    RouterLinkActive,
    TooltipDirective,
    DropdownLoaderDirective,
  ],
  templateUrl: './client-invoice-draft-item.component.html',
  styleUrl: './client-invoice-draft-item.component.scss',
  standalone: true,
})
export class ClientInvoiceDraftItemComponent {
  #capitalizePipe = inject(CapitalizePipe);
  #clientInvoiceDraftItemService = inject(ClientInvoiceDraftItemService);
  #formErrorService = inject(FormErrorService);
  #notificationService = inject(NotificationService);
  #supplierArticleService = inject(SupplierArticleService);
  #translateService = inject(TranslateService);

  clientInvoiceDraftItem = model.required<ClientInvoiceDraftItem>();
  supplier = input.required<string>();
  disabled = input<boolean>(false);
  max = input<number>(Infinity);
  amountReadOnly = input<boolean>(false);

  update = output<boolean>();

  clientInvoiceDraftForm: FormGroup<ClientInvoiceDraftForm> = this.#initForm();

  supplierArticles$: Observable<SupplierArticle[]>;
  updateRows$ = new Subject<{ field: string }>();
  #pendingFields = new Set<string>();
  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    maximumFractionDigits: 2,
    useGrouping: true,
    minimumFractionDigits: 2,
  };
  protected readonly amountFormat = AMOUNT_FORMAT;
  protected readonly amountDecimals = AMOUNT_DECIMALS;
  filterSettings: DropDownFilterSettings = {
    caseSensitive: false,
    operator: 'contains',
  };

  @ViewChild('articleCmb', { static: false }) articleCmb!: ComboBoxComponent;

  constructor() {
    effect(() => {
      const maxVal = this.max();
      const ctrl = this.clientInvoiceDraftForm.controls.amount;
      ctrl.setValidators([Validators.required, Validators.max(maxVal)]);
      ctrl.updateValueAndValidity({ emitEvent: false });
    });

    effect(() => {
      const item = this.clientInvoiceDraftItem();
      if (!item) return;
      const c = this.clientInvoiceDraftForm.controls;
      if (!c.amount.dirty) c.amount.setValue(item.amount, { emitEvent: false });
      if (!c.conditionString.dirty) c.conditionString.setValue(item.conditionString, { emitEvent: false });
      if (!c.purchasePrice.dirty) c.purchasePrice.setValue(item.purchasePrice, { emitEvent: false });
      if (!c.taxPrice.dirty) c.taxPrice.setValue(item.taxPrice, { emitEvent: false });
      if (!c.basicNetPrice.dirty) c.basicNetPrice.setValue(item.basicNetPrice, { emitEvent: false });
      if (!c.retailPrice.dirty) c.retailPrice.setValue(item.retailPrice, { emitEvent: false });
      if (!c.supplierArticle.dirty)
        c.supplierArticle.setValue(item.supplierArticle ?? item.typeNumber ?? null, { emitEvent: false });
      if (!c.articleNumber.dirty) c.articleNumber.setValue(item.articleNumber ?? null, { emitEvent: false });
      if (!c.typeNumber.dirty) c.typeNumber.setValue(item.typeNumber ?? null, { emitEvent: false });
      if (!c.lineTotal.dirty) c.lineTotal.setValue(item.lineTotal ?? null, { emitEvent: false });
      this.clientInvoiceDraftForm.updateValueAndValidity();
    });

    effect(() => {
      if (this.disabled()) {
        this.clientInvoiceDraftForm.disable();
      } else {
        this.clientInvoiceDraftForm.enable();
      }
    });

    effect(() => {
      const item = this.clientInvoiceDraftItem();
      const supplier = this.supplier();
      if (!supplier) return;
      if (!this.supplierArticles$) {
        if (item.supplierArticle) {
          this.supplierArticles$ = getMergedListForIri<SupplierArticle>(
            // this used to be filtered on supplier, but now we show all for edgcase purposes
            //  .getSupplierArticlesBySupplierIri(supplier),
            () => this.#supplierArticleService.getSupplierArticles(),
            () =>
              this.#supplierArticleService
                .getSupplierArticleByIri(item.supplierArticle)
                .pipe(map((response) => [response])),
          );
        } else {
          // this used to be filtered on supplier, but now we show all for edgcase purposes
          //  .getSupplierArticlesBySupplierIri(supplier),
          this.supplierArticles$ = this.#supplierArticleService.getSupplierArticles();
        }
      }
    });

    // Every field left within the debounce is saved in one PATCH; a save in flight is never cancelled.
    this.updateRows$
      .pipe(
        tap(({ field }) => this.#pendingFields.add(field)),
        debounceTime(500),
        concatMap(() =>
          this.#performUpdate(this.#takePendingFields()).pipe(
            catchError((err: HttpErrorResponse) => {
              const notification = this.#notificationService.show(errorToast);
              if (notification.content) {
                notification.content.instance.setCurrentValue(err);
              }
              this.#formErrorService.setErrorViolations(err);
              return EMPTY;
            }),
          ),
        ),
      )
      .subscribe({
        next: () => {
          const notification = this.#notificationService.show(successSaveToast);
          if (notification.content) notification.content.instance.value = 'CLIENT_INVOICE_DRAFT_ITEM';
        },
        error: (err: HttpErrorResponse) => {
          const notification = this.#notificationService.show(errorToast);
          if (notification.content) {
            notification.content.instance.setCurrentValue(err);
          }
          this.#formErrorService.setErrorViolations(err);
        },
      });
  }

  #deleteLine() {
    this.#clientInvoiceDraftItemService.delete(this.clientInvoiceDraftItem()).subscribe({
      next: () => {
        const notification = this.#notificationService.show(successDeleteToast);
        if (notification.content) {
          notification.content.instance.value = this.#translateService.instant('LINE');
        }
        this.update.emit(true);
      },
      error: (err: HttpErrorResponse) => {
        const notification = this.#notificationService.show(errorToast);
        if (notification.content) {
          notification.content.instance.setCurrentValue(err);
        }
      },
    });
  }

  protected menuItems: ActionListItemModel<ActionMenuItemAction>[] = [
    {
      text: this.#translateService.instant('DELETE_ENTITY', {
        entity: this.#capitalizePipe.transform(this.#translateService.instant('LINE')),
      }),
      action: ActionMenuItemAction.DELETE,
      cssClass: 'danger-flat',
      iconClass: 'rc-icon-trash',
    },
  ];

  #initForm() {
    const form = new FormGroup<ClientInvoiceDraftForm>({
      supplierArticle: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      articleNumber: new FormControl(
        { value: '', disabled: true },
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
      amount: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      conditionString: new FormControl('0', {
        nonNullable: true,
        validators: [conditionStringValidator],
      }),
      purchasePrice: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      taxPrice: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      basicNetPrice: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      typeNumber: new FormControl(null, {
        nonNullable: false,
        validators: [],
      }),
      retailPrice: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      lineTotal: new FormControl(
        { value: 0, disabled: true },
        {
          nonNullable: true,
        },
      ),
    });
    form.updateValueAndValidity();
    return form;
  }

  #takePendingFields(): (keyof ClientInvoiceDraftForm)[] {
    const fields = [...this.#pendingFields] as (keyof ClientInvoiceDraftForm)[];
    this.#pendingFields.clear();
    return fields;
  }

  #performUpdate(fields: (keyof ClientInvoiceDraftForm)[]): Observable<ClientInvoiceDraftItem> {
    this.clientInvoiceDraftForm.updateValueAndValidity();
    // the values as they are now (not when the field was left), and only the valid ones
    const sent = fields
      .filter((field) => !this.clientInvoiceDraftForm.controls[field].errors)
      .map((field) => ({ field, value: this.clientInvoiceDraftForm.controls[field].value }));

    if (!sent.length) {
      return of();
    }

    const updateRecord: Record<string, string | number | null | boolean | undefined> = {};
    sent.forEach(({ field, value }) => (updateRecord[field] = value));

    return this.#clientInvoiceDraftItemService
      .update(this.clientInvoiceDraftItem()!['@id']!, updateRecord as unknown as ClientInvoiceDraftItem)
      .pipe(
        tap(() =>
          sent.forEach(({ field, value }) => {
            const ctrl = this.clientInvoiceDraftForm.controls[field];
            ctrl.updateValueAndValidity();
            // typed on while the save was in flight: keep it as an unsaved change
            if (ctrl.value === value) ctrl.markAsPristine();
          }),
        ),
        tap((result) => this.clientInvoiceDraftItem.set(result)),
        tap(() => this.update.emit(true)),
      );
  }

  filterArticles(value: string) {
    this.supplierArticles$ = this.#supplierArticleService.findSupplierArticles(`normalizedCode=${value}`);
  }

  onArticleSelectionChange(article: SupplierArticle): void {
    if (!article) return;
    this.supplierArticles$ = concat(of([article]), this.#supplierArticleService.getSupplierArticles());
  }

  getData(entries: SupplierArticle[] | null) {
    return entries ?? [];
  }

  onActionMenuItemClick(actionMenuItem: ActionListItemModel<ActionMenuItemAction | string>): void {
    switch (actionMenuItem.action) {
      case ActionMenuItemAction.DELETE:
        this.#deleteLine();
        break;
    }
  }

  updateRow(field: string): void {
    this.clientInvoiceDraftForm.updateValueAndValidity();
    if (this.clientInvoiceDraftForm.controls[field as keyof ClientInvoiceDraftForm].dirty) {
      this.updateRows$.next({ field });
      this.clientInvoiceDraftForm.updateValueAndValidity();
    }
  }

  updateSupplierArticle(): void {
    const typedText = (this.articleCmb?.text ?? '').trim();
    console.log('typedText', typedText);
    if (!typedText) return;
    const id = this.clientInvoiceDraftItem()!['@id']!;

    this.supplierArticles$.pipe(take(1)).subscribe((articles) => {
      const list = articles ?? [];
      const controlValue = this.clientInvoiceDraftForm.controls.supplierArticle.value;
      console.log('controlValue', controlValue);
      const pickedExisting = typeof controlValue === 'string' && list.some((a) => a['@id'] === controlValue);
      console.log('pickedExisting', pickedExisting);

      if (pickedExisting) {
        this.updateRow('supplierArticle');
      } else {
        this.#clientInvoiceDraftItemService.update(id, { typeNumber: typedText } as ClientInvoiceDraftItem).subscribe({
          next: (response) => {
            this.clientInvoiceDraftForm.markAsPristine();
            this.update.emit(true);
            this.clientInvoiceDraftItem.set(response);
            const n = this.#notificationService.show(successSaveToast);
            if (n.content) n.content.instance.value = 'CLIENT_INVOICE_DRAFT_ITEM';
          },
          error: (err) => {
            const n = this.#notificationService.show(errorToast);
            if (n.content) n.content.instance.setCurrentValue(err);
            this.#formErrorService.setErrorViolations(err as any);
          },
        });
      }
    });
  }

  getTextPixelWidth(text: string, element: HTMLElement): number {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d')!;

    const style = getComputedStyle(element);
    ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    return ctx.measureText(text).width;
  }
}

interface ClientInvoiceDraftForm {
  supplierArticle: FormControl<string | null>;
  articleNumber: FormControl<string | null>;
  amount: FormControl<number>;
  conditionString: FormControl<string>;
  purchasePrice: FormControl<number>;
  taxPrice: FormControl<number>;
  basicNetPrice: FormControl<number>;
  typeNumber: FormControl<string | null>;
  retailPrice: FormControl<number>;
  lineTotal: FormControl<number>;
}
